import { useEffect, useState } from "react"
import { useNavigate, useLocation, useSearchParams } from "react-router-dom"
import { createDevShortcutSession, getAssessmentSession } from "~/api/assessmentSessions"
import type { AssessmentSessionResponse } from "~/api/assessmentSessions"
import { assetUrl } from "~/utils/assetUrl"
import { PageShell } from "~/components/PageShell"

type RevealState = { sessionToken?: string; result?: AssessmentSessionResponse } | null

export function RevealPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const state = location.state as RevealState
  const tokenFromUrl = searchParams.get("token")
  const [loadedResult, setLoadedResult] = useState<AssessmentSessionResponse | null>(null)
  const result = state?.result ?? loadedResult
  const sessionToken = state?.sessionToken ?? loadedResult?.session_token ?? tokenFromUrl ?? undefined

  const devType =
    import.meta.env.DEV && !state?.result && !loadedResult && !tokenFromUrl
      ? searchParams.get("type")
      : null

  useEffect(() => {
    if (result) return

    if (devType) {
      createDevShortcutSession(devType, searchParams.get("name") ?? undefined)
        .then(setLoadedResult)
        .catch(() => {
          navigate("/results", { replace: true })
        })
      return
    }

    const token = state?.sessionToken ?? tokenFromUrl
    if (token) {
      getAssessmentSession(token)
        .then(setLoadedResult)
        .catch(() => {
          navigate(`/results?token=${encodeURIComponent(token)}`, { replace: true })
        })
      return
    }

    navigate("/results", { replace: true })
  }, [result, state?.sessionToken, tokenFromUrl, navigate, devType, searchParams])

  function handleContinue() {
    const path = sessionToken
      ? `/results?token=${encodeURIComponent(sessionToken)}`
      : "/results"
    navigate(path, { state: state ?? undefined })
  }

  if (!result) {
    return (
      <PageShell>
        <p className="pt-4 text-[var(--color-text-secondary)]">Loading...</p>
      </PageShell>
    )
  }

  const dogName = result.dog_name
  const archetype = result.archetype

  const archetypeName = archetype?.name ?? result.archetype_label ?? "your dog"
  const primaryHook = archetype?.hook_primary ?? result.archetype_explanation ?? ""
  const secondaryHook = archetype?.hook_secondary ?? ""
  const iconPath = archetype ? assetUrl(`/images/archetypes/logos/${archetype.icon}`) : null

  return (
    <PageShell>
      <div className="flex w-full max-w-md flex-col items-center text-center">
        {iconPath && (
          <div className="flex aspect-square w-full max-w-[120px] items-center justify-center rounded-xl bg-[var(--color-surface-card)]">
            <img
              src={iconPath}
              alt={archetypeName}
              className="h-20 w-20 max-h-full max-w-full object-contain"
            />
          </div>
        )}

        <h1 className="mt-7 text-3xl font-semibold leading-tight text-[var(--color-text-primary)]">
          Meet {dogName} {archetypeName}
        </h1>

        <p className="mt-3 text-base font-medium text-[var(--color-text-primary)]">
          Your dog&apos;s personality is just the beginning.
        </p>
        <p className="mt-3 text-sm leading-relaxed text-[var(--color-text-secondary)]">
          Keep exploring below for personalised guidance, answers and tips to build a deeper bond
          with {dogName}.
        </p>
        <p className="mt-4 text-base leading-relaxed text-[var(--color-text-secondary)]">
          {primaryHook || "Your dog's unique personality profile is ready."}
        </p>
        {secondaryHook && (
          <p className="mt-3 text-base leading-relaxed text-[var(--color-text-secondary)]">
            {secondaryHook}
          </p>
        )}

        <button
          type="button"
          onClick={handleContinue}
          className="mt-5 rounded-lg bg-[var(--color-text-primary)] px-6 py-3 font-medium text-[var(--color-surface-card)] transition hover:opacity-90"
        >
          Start Understanding {dogName}
        </button>
      </div>
    </PageShell>
  )
}
